/**
 * Traffic Classification Service
 * Handles traffic classification logic from DATABASE.txt and Road_Info.txt
 * Implements Excel VLOOKUP logic for reliability range validation
 *
 * Data source: ../data/traffic-classification.json (centralized configuration)
 */

import { logDebug } from '../utils/utils';
import { LogTag } from '../config/loggingConfig';
import trafficData from '../data/traffic-classification.json';

export interface ReliabilityRange {
    urban: string; // Reliability range for urban roads
    rural: string; // Reliability range for rural roads
    minReliability: number;
    maxReliability: number;
}

export interface FunctionalClassificationData {
    classification: string;
    urban: string;
    rural: string;
    esalRange?: string;
    minEsal?: number;
    maxEsal?: number;
}

export class TrafficClassificationService {
    // Data loaded from centralized JSON configuration (../data/traffic-classification.json)
    // Replica della tabella DATABASE!B7:D10 from Excel
    private static readonly FUNCTIONAL_CLASSIFICATION_TABLE: Record<string, ReliabilityRange> = trafficData.roadClassifications as Record<string, ReliabilityRange>;

    // Airport-specific reliability ranges based on airport type (International/Regional/Domestic)
    // For airports, the range depends only on roadType (which contains airport_type value)
    private static readonly AIRPORT_RELIABILITY_TABLE: Record<string, { range: string; minReliability: number; maxReliability: number }> = trafficData.airportClassifications as Record<
        string,
        { range: string; minReliability: number; maxReliability: number }
    >;

    /**
     * Get reliability range for a functional classification and road type
     * Implements Excel VLOOKUP logic from Road_Info!D16
     * Excel: =VLOOKUP(D14,DATABASE!B7:D10,IF(D15=DATABASE!C6,2,IF(D15=DATABASE!D6,3,)),)
     * @param functionalClassification - D14 value (functional classification)
     * @param roadType - D15 value (Urban/Rural)
     * @returns Reliability range string and validation bounds
     */
    public static getReliabilityRange(functionalClassification: string, roadType: string): { range: string; minReliability: number; maxReliability: number } {
        // Check if this is an Airport project (roadType will be International/Regional/Domestic)
        const airportData = this.AIRPORT_RELIABILITY_TABLE[roadType];
        if (airportData) {
            logDebug('TrafficClassificationService', `Using airport reliability mapping for type: ${roadType}`, { airportData }, LogTag.FORM_CASCADE);
            return airportData;
        }

        // Otherwise, use road classification table
        const classificationData = this.FUNCTIONAL_CLASSIFICATION_TABLE[functionalClassification];

        if (!classificationData) {
            logDebug('TrafficClassificationService', `Unknown functional classification: ${functionalClassification}, using ${trafficData.defaultClassification} defaults`);
            const defaultData = this.FUNCTIONAL_CLASSIFICATION_TABLE[trafficData.defaultClassification];
            return {
                range: roadType === 'Urban' ? defaultData.urban : defaultData.rural,
                minReliability: defaultData.minReliability,
                maxReliability: defaultData.maxReliability,
            };
        }

        // Excel VLOOKUP logic: column 2 for Urban (C6), column 3 for Rural (D6)
        const range = roadType === 'Urban' ? classificationData.urban : classificationData.rural;

        return {
            range,
            minReliability: classificationData.minReliability,
            maxReliability: classificationData.maxReliability,
        };
    }

    /**
     * Validate reliability level against functional classification and road type
     * @param reliabilityLevel - The reliability level to validate (%)
     * @param functionalClassification - Functional classification
     * @param roadType - Road type (Urban/Rural)
     * @returns Validation result with warnings if needed
     */
    public static validateReliabilityLevel(reliabilityLevel: number, functionalClassification: string, roadType: string): { isValid: boolean; warning?: string; recommendedRange: string } {
        const { range, minReliability, maxReliability } = this.getReliabilityRange(functionalClassification, roadType);

        const isValid = reliabilityLevel >= minReliability && reliabilityLevel <= maxReliability;

        const result = {
            isValid,
            recommendedRange: range,
            warning: undefined as string | undefined,
        };

        if (!isValid) {
            if (reliabilityLevel < minReliability) {
                result.warning = `Reliability level ${reliabilityLevel}% is below recommended minimum of ${minReliability}% for ${functionalClassification} (${roadType})`;
            } else {
                result.warning = `Reliability level ${reliabilityLevel}% exceeds recommended maximum of ${maxReliability}% for ${functionalClassification} (${roadType})`;
            }
        }

        logDebug('TrafficClassificationService', 'Reliability validation:', {
            input: { reliabilityLevel, functionalClassification, roadType },
            result,
        });

        return result;
    }

    /**
     * Get all available functional classifications
     * @returns Array of functional classification options
     */
    public static getFunctionalClassifications(): string[] {
        return Object.keys(this.FUNCTIONAL_CLASSIFICATION_TABLE);
    }

    /**
     * Get functional classification data for UI display
     * @param functionalClassification - Functional classification
     * @returns Classification data with ranges for both road types
     */
    public static getFunctionalClassificationData(functionalClassification: string): FunctionalClassificationData | null {
        const data = this.FUNCTIONAL_CLASSIFICATION_TABLE[functionalClassification];
        if (!data) {
            return null;
        }

        return {
            classification: functionalClassification,
            urban: data.urban,
            rural: data.rural,
        };
    }
}

export default TrafficClassificationService;
